import React from "react";
import { motion } from "framer-motion";

// Award-winning easing curves
const easeOut = [0.22, 1, 0.36, 1];
const easeInOut = [0.65, 0, 0.35, 1];

/**
 * Basic Reveal — fade + slide-up when scrolled into view.
 * Slower duration + larger initial offset gives a more premium, considered feel.
 */
export function Reveal({ children, delay = 0, y = 32, className = "", once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: "-80px" }}
      transition={{ duration: 0.9, delay, ease: easeOut }}
    >
      {children}
    </motion.div>
  );
}

/**
 * MaskReveal — Vercel/Apple-style clip-path reveal. Content is masked from below,
 * unmasks with a fluid clip-path animation. Feels like the copy is rising from behind a curtain.
 */
export function MaskReveal({ children, delay = 0, className = "", once = true }) {
  return (
    <motion.span
      className={`inline-block overflow-hidden align-baseline ${className}`}
      style={{ willChange: "clip-path" }}
      initial={{ clipPath: "inset(100% 0 0 0)" }}
      whileInView={{ clipPath: "inset(0% 0 0 0)" }}
      viewport={{ once, margin: "-60px" }}
      transition={{ duration: 1.15, delay, ease: easeInOut }}
    >
      <motion.span
        className="inline-block"
        initial={{ y: "0.9em" }}
        whileInView={{ y: "0em" }}
        viewport={{ once, margin: "-60px" }}
        transition={{ duration: 1.15, delay, ease: easeInOut }}
      >
        {children}
      </motion.span>
    </motion.span>
  );
}

/**
 * WordReveal — split children into words and reveal each in a staggered mask.
 * Best for hero headlines.
 */
export function WordReveal({ text, delay = 0, className = "", stagger = 0.06, once = true }) {
  const words = String(text).split(" ");
  return (
    <span className={className} aria-label={text}>
      {words.map((w, i) => (
        <React.Fragment key={i}>
          <MaskReveal delay={delay + i * stagger} once={once}>{w}</MaskReveal>
          {i < words.length - 1 && "\u00A0"}
        </React.Fragment>
      ))}
    </span>
  );
}

export function StaggerGroup({ children, className = "", stagger = 0.09 }) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      variants={{ show: { transition: { staggerChildren: stagger } } }}
    >
      {children}
    </motion.div>
  );
}

export const staggerItem = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: easeOut } },
};
